import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Send, Paperclip, X } from "lucide-react";
import { SectionHead } from "../components/SectionHead";
import { Reveal, stagger, item } from "../lib/motion";
import { CULTURE } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALLOWED_CV = [".pdf", ".doc", ".docx", ".rtf", ".odt"];
const MAX_CV_MB = 10;

const field =
  "w-full bg-cream border border-ink/15 px-4 py-3.5 font-sans text-ink placeholder-slate/50 focus:border-gold transition-colors duration-200 outline-none";
const labelCls = "font-mono text-xs uppercase tracking-[0.14em] text-slate mb-2 block";

export default function Careers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", discipline: "", experience: "", linkedin: "", message: "" });
  const [cvFile, setCvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    document.title = "Careers — PROSPEC Consultancy";
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_CV.includes(ext)) {
      toast.error("Please upload a PDF, DOC, DOCX, RTF or ODT file.");
      e.target.value = "";
      return;
    }
    if (f.size > MAX_CV_MB * 1024 * 1024) {
      toast.error(`File is too large (max ${MAX_CV_MB} MB).`);
      e.target.value = "";
      return;
    }
    setCvFile(f);
  };

  const clearFile = () => {
    setCvFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.discipline || !form.message) {
      toast.error("Please complete your name, email, discipline and message.");
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (cvFile) data.append("cv", cvFile);
      await axios.post(`${API}/careers`, data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Thanks for reaching out — we've received your details and will be in touch.");
      setForm({ name: "", email: "", phone: "", discipline: "", experience: "", linkedin: "", message: "" });
      clearFile();
    } catch (err) {
      toast.error("Something went wrong. Please email us at hr@prospecconsultancy.com.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20">
      {/* BANNER */}
      <section className="relative bg-ink text-cream grain overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.12] bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.unsplash.com/photo-1709934645644-f055bb2b75de?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600)" }}
        />
        <div className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32 relative">
          <p className="eyebrow mb-6">Careers</p>
          <h1 className="font-display font-bold text-cream text-4xl md:text-6xl leading-[1.02] max-w-4xl">
            Real ownership from day one — not a cog in a machine.
          </h1>
          <p className="text-cream/70 text-lg mt-8 max-w-2xl leading-relaxed">
            PROSPEC is a growing specialist team. If you join, you own live programmes, sit close to
            clients, and learn the full breadth of planning and project controls — fast.
          </p>
        </div>
      </section>

      {/* CULTURE STATS */}
      <section className="bg-sand border-b border-ink/10">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="max-w-container mx-auto px-6 md:px-12 lg:px-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-ink/10"
        >
          {CULTURE.map((c) => (
            <motion.div key={c.label} variants={item} className="bg-sand py-12 px-6 text-center">
              <div className="font-display text-3xl md:text-4xl text-ink mb-2">{c.stat}</div>
              <p className="font-mono text-xs text-slate tracking-wide uppercase">{c.label}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* WHY + INTENT */}
      <section className="max-w-container mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-14">
          <div>
            <SectionHead
              eyebrow="Who we want to hear from"
              title="Planners, controllers, and construction professionals."
            />
            <div className="mt-8 space-y-6 text-slate text-lg leading-relaxed">
              <p>
                We are always interested in hearing from planning engineers, project controls
                specialists, and experienced construction professionals who take the programme as
                seriously as we do.
              </p>
              <p>
                We are not advertising specific roles right now — we are building our team steadily
                and deliberately. If you are strong on baseline development, progress control, delay
                analysis or reporting, we want to know you.
              </p>
              <p className="text-ink font-medium">
                Send us your details below and tell us how you think. We read every message.
              </p>
              <p className="text-slate text-base">
                Prefer to reach out directly? Email{" "}
                <a href="mailto:hr@prospecconsultancy.com" data-testid="careers-hr-email" className="text-teal underline underline-offset-4 hover:text-gold transition-colors">hr@prospecconsultancy.com</a>{" "}
                or message us on WhatsApp at{" "}
                <a href="https://wa.me/447827229323" target="_blank" rel="noopener noreferrer" data-testid="careers-whatsapp" className="text-teal underline underline-offset-4 hover:text-gold transition-colors">+44 7827 229323</a>.
              </p>
            </div>
          </div>

          {/* APPLICATION FORM */}
          <Reveal delay={0.1}>
            <form onSubmit={submit} data-testid="careers-form" className="border border-ink/15 bg-cream p-8 md:p-10 space-y-6">
              <p className="eyebrow mb-2">General application</p>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="k-name">Name *</label>
                  <input id="k-name" data-testid="careers-name" className={field} value={form.name} onChange={set("name")} placeholder="Your full name" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="k-email">Email *</label>
                  <input id="k-email" type="email" data-testid="careers-email" className={field} value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="k-phone">Phone</label>
                  <input id="k-phone" data-testid="careers-phone" className={field} value={form.phone} onChange={set("phone")} placeholder="Optional" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="k-discipline">Discipline *</label>
                  <input id="k-discipline" data-testid="careers-discipline" className={field} value={form.discipline} onChange={set("discipline")} placeholder="e.g. Planning Engineer" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className={labelCls} htmlFor="k-exp">Experience</label>
                  <input id="k-exp" data-testid="careers-experience" className={field} value={form.experience} onChange={set("experience")} placeholder="e.g. 6 years" />
                </div>
                <div>
                  <label className={labelCls} htmlFor="k-linkedin">LinkedIn / Portfolio</label>
                  <input id="k-linkedin" data-testid="careers-linkedin" className={field} value={form.linkedin} onChange={set("linkedin")} placeholder="Optional link" />
                </div>
              </div>
              <div>
                <label className={labelCls} htmlFor="k-message">Why PROSPEC? *</label>
                <textarea id="k-message" rows={4} data-testid="careers-message" className={field} value={form.message} onChange={set("message")} placeholder="Tell us about your experience and how you approach planning & controls." />
              </div>
              <div>
                <label className={labelCls} htmlFor="k-cv">CV / Resume</label>
                {!cvFile ? (
                  <label
                    htmlFor="k-cv"
                    data-testid="careers-cv-dropzone"
                    className="flex items-center gap-3 w-full bg-cream border border-dashed border-ink/25 px-4 py-3.5 cursor-pointer hover:border-gold transition-colors duration-200"
                  >
                    <Paperclip size={18} className="text-teal shrink-0" />
                    <span className="font-sans text-slate/70 text-sm">Attach your CV — PDF, DOC, DOCX, RTF or ODT (max {MAX_CV_MB} MB)</span>
                  </label>
                ) : (
                  <div className="flex items-center justify-between gap-3 w-full bg-sand border border-ink/15 px-4 py-3.5">
                    <span className="flex items-center gap-3 min-w-0">
                      <Paperclip size={18} className="text-teal shrink-0" />
                      <span data-testid="careers-cv-name" className="font-sans text-ink text-sm truncate">{cvFile.name}</span>
                    </span>
                    <button type="button" data-testid="careers-cv-remove" onClick={clearFile} className="text-slate hover:text-ink shrink-0" aria-label="Remove file">
                      <X size={18} />
                    </button>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  id="k-cv"
                  type="file"
                  data-testid="careers-cv"
                  accept=".pdf,.doc,.docx,.rtf,.odt,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={onFile}
                  className="hidden"
                />
              </div>
              <motion.button
                type="submit"
                data-testid="careers-submit"
                disabled={loading}
                whileTap={{ scale: 0.98 }}
                className="btn-sheen inline-flex items-center gap-2 bg-gold text-ink px-8 py-4 font-mono text-xs uppercase tracking-[0.18em] hover:-translate-y-0.5 transition-transform duration-300 disabled:opacity-60"
              >
                {loading ? "Sending…" : <>Submit application <Send size={15} /></>}
              </motion.button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
